<script setup lang="ts">
import type { EventStoryblok } from '@/types'
import { EventContextType } from '@/types'
import { useEvents } from '@/composables/useEvents'
import { useScrollToElement } from '@/composables/useScrollToElement'

interface Props {
  event: EventStoryblok
  eventSlug: string
}

const props = defineProps<Props>()
const { representatives, hasBooth } = useEvents(props.event)
const { scrollToElement } = useScrollToElement()
const isExpanded = ref(false)
const representativesMobileModifier = computed(
  () => isExpanded.value && 'event-card--representatives-visible',
)
const eventInfoContext = computed(() =>
  props.event.is_featured ? EventContextType.FEATURED : EventContextType.BASE,
)
const featuredEventModifier = computed(
  () => props.event.is_featured && 'event-card--featured-event',
)
const hasEventPage = computed(() => props.event.generate_event_page)

const handleExpandedButton = async (payload: boolean) => {
  const id = isExpanded.value
    ? `${props.event.title}`
    : `${props.event.title}-representatives`
  isExpanded.value = payload
  await nextTick()
  scrollToElement(id)
}
</script>

<template>
  <div
    :id="`${slugify(`${event.title}`)}`"
    :class="[
      'event-card',
      featuredEventModifier,
      representativesMobileModifier,
    ]"
  >
    <section class="event-card__info-container">
      <EventInfo
        :event="event"
        :context="eventInfoContext"
        :has-booth="hasBooth"
      />
      <div class="event-card__buttons-container">
        <a
          class="event-card__cta button"
          :href="event.cta_link"
          target="_blank"
        >
          {{ $t('common.form.schedule_meeting') }}
        </a>
        <BaseButton
          v-if="hasEventPage"
          :href="{
            cached_url: `/events/${eventSlug}`,
          }"
          outlined
          class="event-card__cta"
          :title="$t('common.events.learn_more')"
        >
          <template #text>
            {{ $t('common.events.learn_more') }}
          </template>
        </BaseButton>
      </div>
    </section>
    <section class="event-card__representatives-container">
      <div class="event-card__representatives-preview">
        <EventRepresentativesPreview
          :id="`${event._uid}-preview`"
          :is-featured="event.is_featured"
          :representatives="representatives"
          @click="handleExpandedButton"
        />
      </div>
      <div class="event-card__representatives">
        <EventRepresentatives
          :id="`${event._uid}-representatives`"
          :is-featured="event.is_featured"
          :representatives="representatives"
          @click="handleExpandedButton"
        />
      </div>
    </section>
  </div>
</template>

<style lang="scss" scoped>
.event-card {
  $self: &;

  overflow: hidden;
  padding: 40px 32px;
  border-radius: 16px;
  background: white;
  position: relative;
  z-index: 10;

  @media (min-width: $breakpoint-lg) {
    padding: 80px;
    display: grid;
    grid-template-columns: 1fr 338px;
    grid-template-rows: min-content 1fr;
    gap: 48px 40px;
  }

  &__image {
    grid-column: 1;
  }

  &__logo {
    margin-bottom: 48px;

    @media (min-width: $breakpoint-lg) {
      margin: 0;
    }
  }

  &__heading {
    margin-bottom: 24px;

    @include heading-4;

    @media (min-width: $breakpoint-lg) {
      @include heading-3;

      margin-bottom: 40px;
    }
  }

  &__info-container {
    display: grid;
    gap: var(--space40);

    --image-variant: contain;

    @media (min-width: $breakpoint-lg) {
      gap: var(--space48);
    }
  }

  &__buttons-container {
    display: grid;
    margin-bottom: var(--space48);
    gap: var(--space24);

    @media (min-width: $breakpoint-lg) {
      grid-template-columns: repeat(2, min-content);
      margin: 0;
      gap: 10px;
    }
  }

  &::after {
    content: '';
    position: absolute;
    width: 100%;
    bottom: 0;
    left: 0;
    border-radius: 0 0 16px 16px;
    height: 80px;
    background: var(--cobalt-800);
    z-index: 0;

    @media (min-width: $breakpoint-lg) {
      height: 100%;
      width: 148px;
      left: calc(100% - 148px);
      border-radius: 0 16px 16px 0;
    }
  }

  &__representatives-container {
    @media (min-width: $breakpoint-lg) {
      grid-column: 2 / -1;
      grid-row: 1 / -1;
      display: grid;
      place-items: center;
    }
  }

  &__representatives {
    display: none;

    @media (min-width: $breakpoint-lg) {
      display: grid;
    }
  }

  &__representatives-preview {
    @media (min-width: $breakpoint-lg) {
      display: none;
    }
  }

  &--featured-event {
    &::after {
      background: var(--blue-600);

      @media (min-width: $breakpoint-lg) {
        left: calc(100% - 151px);
        width: 151px;
        top: 80px;
        border-radius: 24px 0;
      }
    }

    #{ $self }__representatives-container {
      @media (min-width: $breakpoint-lg) {
        place-items: end;
        padding-top: 46px;
      }
    }
  }

  &--representatives-visible {
    #{$self}__representatives-preview {
      display: none;
    }
    #{$self}__representatives {
      display: block;
    }
  }
}
</style>
